import { useEffect, useState } from "react";
import { X, ExternalLink, Copy, Mail, CheckCircle2, Send, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function PaymentModal({ open, onClose, packInfo, checkoutData, onSubmitted }) {
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState("choose"); // choose | confirm | done
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      setStep("choose");
      setCode("");
      setBusy(false);
    }
  }, [open]);

  if (!open || !packInfo || !checkoutData) return null;

  const { credits, amount_eur, paypal_url, contact_email, label } = checkoutData;

  const openPayPal = () => {
    if (!paypal_url) {
      toast.error("Le lien PayPal n'est pas configuré. Contactez l'administrateur.");
      return;
    }
    window.open(paypal_url, "_blank", "noopener,noreferrer");
    setStep("confirm");
  };

  const openEmail = () => {
    if (!contact_email) {
      toast.error("Aucun email de contact configuré.");
      return;
    }
    const subject = encodeURIComponent(`Demande d'achat — ${label}`);
    const body = encodeURIComponent(
      `Bonjour,\n\nJe souhaite acheter ${credits} crédits (pack à ${amount_eur.toFixed(2)}€ TTC).\n\nJe vais effectuer le paiement par PayPal / virement.\n\nMerci de me confirmer la marche à suivre et de créditer mon compte.\n\nCordialement.`
    );
    window.location.href = `mailto:${contact_email}?subject=${subject}&body=${body}`;
  };

  const copyPayPal = async () => {
    if (!paypal_url) return;
    try {
      await navigator.clipboard.writeText(paypal_url);
      setCopied(true);
      toast.success("Lien PayPal copié");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const submitConfirmation = async () => {
    if (!code.trim() || code.trim().length < 3) {
      toast.error("Code trop court. Vérifiez sur votre email PayPal.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/credits/confirm-payment", {
        pack_key: packInfo.key,
        confirmation_code: code.trim(),
      });
      setStep("done");
      toast.success(data.message);
      if (onSubmitted) onSubmitted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="payment-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-zinc-950 border border-zinc-800 w-full max-w-lg mx-4 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="payment-modal-close"
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 border-b border-zinc-800">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-500 mb-2">Récapitulatif</div>
          <div className="font-display text-2xl font-bold tracking-tight">{label}</div>
          <div className="flex items-baseline gap-2 mt-3">
            <div className="font-display text-5xl font-extrabold tracking-tighter">{amount_eur.toFixed(2)}</div>
            <div className="text-zinc-500 text-lg">€ TTC</div>
            <div className="text-xs font-mono text-zinc-500 ml-auto">
              {credits.toLocaleString("fr-FR")} crédits
            </div>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="flex border-b border-zinc-800">
          {["choose", "confirm", "done"].map((s, i) => {
            const isActive = step === s;
            const isPast = ["choose", "confirm", "done"].indexOf(step) > i;
            return (
              <div key={s} className={`flex-1 px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-center border-r last:border-r-0 border-zinc-800 ${
                isActive ? "bg-red-500 text-white" : isPast ? "bg-zinc-900 text-green-400" : "text-zinc-600"
              }`}>
                {i + 1}. {s === "choose" ? "Payer" : s === "confirm" ? "Confirmer" : "Envoyé"}
              </div>
            );
          })}
        </div>

        {/* STEP 1 — Choose payment method */}
        {step === "choose" && (
          <div className="p-8 space-y-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">Choisir un mode de paiement</div>

            {/* PayPal option */}
            <div className="border border-zinc-800 hover:border-red-500/40 p-5 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="font-display text-lg font-bold text-white flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#009cde"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.02a.77.77 0 0 1 .76-.638h6.783c2.238 0 3.94.464 4.933 1.375.951.871 1.223 2.053.94 3.68a5.9 5.9 0 0 0-.06.4c-.833 4.283-3.723 5.762-7.408 5.762h-1.867c-.442 0-.815.3-.884.717l-.984 6.16z"/></svg>
                    PayPal
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">Paiement instantané par CB ou compte PayPal</div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Button
                    data-testid="pay-with-paypal-btn"
                    onClick={openPayPal}
                    disabled={!paypal_url}
                    className="bg-[#009cde] hover:bg-[#0080b8] rounded-none text-white"
                    size="sm"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Payer
                  </Button>
                  {paypal_url && (
                    <button
                      data-testid="copy-paypal-link"
                      onClick={copyPayPal}
                      className="text-[10px] font-mono text-zinc-500 hover:text-white flex items-center gap-1 justify-center"
                    >
                      {copied ? <><CheckCircle2 className="w-3 h-3" /> Copié</> : <><Copy className="w-3 h-3" /> Copier le lien</>}
                    </button>
                  )}
                </div>
              </div>
              {!paypal_url && (
                <div className="text-[10px] text-orange-400 mt-2 font-mono">
                  PayPal non configuré — utilisez l'option email ci-dessous
                </div>
              )}
            </div>

            {/* Email option */}
            <div className="border border-zinc-800 hover:border-red-500/40 p-5 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="font-display text-lg font-bold text-white flex items-center gap-2">
                    <Mail className="w-5 h-5 text-red-500" /> Contact par email
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">Envoyez un email — nous vous confirmerons la marche à suivre</div>
                  {contact_email && (
                    <div className="text-[10px] font-mono text-zinc-500 mt-2">→ {contact_email}</div>
                  )}
                </div>
                <Button
                  data-testid="pay-with-email-btn"
                  onClick={openEmail}
                  disabled={!contact_email}
                  className="bg-red-500 hover:bg-red-600 rounded-none"
                  size="sm"
                >
                  <Mail className="w-3.5 h-3.5 mr-1.5" /> Écrire
                </Button>
              </div>
            </div>

            <button
              data-testid="already-paid-link"
              onClick={() => setStep("confirm")}
              className="w-full text-center text-xs font-mono text-zinc-400 hover:text-white underline underline-offset-4 pt-2"
            >
              J'ai déjà payé, saisir mon code de confirmation →
            </button>
          </div>
        )}

        {/* STEP 2 — Confirm with PayPal code */}
        {step === "confirm" && (
          <div className="p-8 space-y-4">
            <div className="border-l-2 border-yellow-500 bg-yellow-500/5 pl-4 py-3">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
                <div className="text-sm text-zinc-300">
                  <div className="font-semibold text-yellow-400 mb-1">Vous venez de payer sur PayPal ?</div>
                  Collez le <b>code de transaction</b> reçu par email PayPal (ex: <span className="font-mono text-xs">1AB23456CD789012E</span>). Vos crédits seront activés dès validation par l'admin.
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Code de transaction PayPal</label>
              <Input
                data-testid="confirmation-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitConfirmation()}
                placeholder="Ex: 1AB23456CD789012E"
                className="rounded-none bg-zinc-900 border-zinc-800 font-mono uppercase text-white"
                autoFocus
              />
              <div className="text-[10px] text-zinc-500 mt-2 font-mono">
                💡 Où trouver ce code ? Dans l'email de confirmation PayPal, ou sur paypal.com → Activité
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                data-testid="back-to-choose-btn"
                onClick={() => setStep("choose")}
                variant="outline"
                className="rounded-none border-zinc-700 bg-transparent text-zinc-300"
              >
                Retour
              </Button>
              <Button
                data-testid="submit-confirmation-btn"
                onClick={submitConfirmation}
                disabled={busy || !code.trim()}
                className="flex-1 bg-red-500 hover:bg-red-600 rounded-none"
              >
                <Send className="w-4 h-4 mr-2" />
                {busy ? "Envoi…" : "Envoyer pour validation"}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 — Done */}
        {step === "done" && (
          <div className="p-8 text-center space-y-4">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
            <div className="font-display text-2xl font-bold text-white">Code envoyé !</div>
            <div className="text-sm text-zinc-400 leading-relaxed max-w-sm mx-auto">
              Votre paiement va être vérifié <b className="text-white">sous 24h (jours ouvrés)</b>. Vous recevrez un email dès que vos crédits seront activés sur votre compte.
            </div>
            <Button
              data-testid="close-done-btn"
              onClick={onClose}
              className="bg-zinc-800 hover:bg-zinc-700 rounded-none mt-4"
            >
              Fermer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
